import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useBannerOffset } from "@hooks/useBannerOffset";

import { bannerSurface } from "@utils/bannerGradient";

import { ERoutes } from "@/utils/constants";

/**
 * Site-wide blue notice for the beta. It says one of two things, decided by
 * where the reader is.
 *
 * **The ask** — on the catalogue, the account area and the contact forms:
 * "Make Audio Sanctum better." with a Report-a-bug button. These are the pages
 * where someone is actually using the product rather than reading about it, so
 * they are the pages where a bug is fresh in mind and the ask is answerable.
 * It asks rather than announces — "Audio Sanctum is currently in beta" is a
 * statement about us and gives the reader nothing to do.
 *
 * **The welcome** — everywhere else: "Welcome to the new Audio Sanctum." with a
 * star, and no button. On a page someone is still reading, an ask for bug
 * reports is a demand made of a visitor who has not used anything yet.
 *
 * The icon on the ask is a bug, not an envelope: the button is not "write to
 * us", it is "report the thing you just hit", and the icon should say which.
 *
 * Unlike every other global banner, this one has no trigger: it shows to
 * everyone, signed in or not, from the first page load. So the dismissal is the
 * only thing keeping it from being permanent furniture — it is remembered
 * against a version key, which lets a later beta phase raise it again without
 * having to guess which browsers still hold a stale flag.
 *
 * Blue on purpose. Every other banner in this family means something is wrong,
 * needs attention, or went well; this one means none of those. It is telling
 * you where the product is, and reads as information rather than as a state to
 * resolve.
 *
 * Dismissing it does not remove it — it MOVES it. The notice collapses out of
 * the header and reappears above the footer, where it stays on every page for
 * the rest of the beta. The reasoning: this is not an alert someone clears, it
 * is a standing fact about the product, and a bug report is worth more late in
 * a visit than early. Clicking the X should mean "stop putting this in front of
 * me", not "I will never need this", so it keeps a home rather than vanishing.
 *
 * Two instances of this component, one at each placement. Which one draws is
 * decided by the same flag, so they can never both be on screen.
 *
 * Same mechanics as the rest of the family: the same 360ms collapse, and the
 * top placement publishes its height into `--beta-banner-h` so the fixed
 * header's offset reserves the strip instead of letting content slide behind.
 * The bottom placement sits in normal flow above the footer and reserves
 * nothing — there is nothing below it to push.
 */
const DISMISS_KEY = "beta-banner-dismissed-v1";
/** Both instances read one flag, so the one that is leaving has to tell the one
    that is arriving. `storage` only fires in OTHER tabs, which is the opposite
    of what is needed here. */
const MOVED_EVENT = "beta-banner-moved";

/** Pages where the reader is using the product, so a bug is fresh in mind. */
const ASK_ROUTES: string[] = [ERoutes.Catalog, ERoutes.Contact, ERoutes.Licensing, ERoutes.Submit];
/** Same, for route families: the whole account area and every contact form.
    Matched on a segment boundary so `/account-deleted` is not `/account`. */
const ASK_PREFIXES = ["/account", "/contact-licensing"];

const wantsTheAsk = (pathname: string) =>
  ASK_ROUTES.includes(pathname) ||
  ASK_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/** Pages that will not carry the notice at the TOP. The claims page opens on
    its own stack — account issue banners, the channel picker, the tab bar — and
    a strip above all of that pushes the thing someone came for below the fold.
    They still get it above the footer once it has been moved there, which is
    where a bug report is worth more anyway. */
const NO_TOP_ROUTES: string[] = [ERoutes.AccountVideos];

/** Pages that carry the notice NOWHERE, top or bottom.
 *
 *  The order-success page is the receipt for money that has just left someone's
 *  card. It has one job — tell them the purchase worked and what they now have —
 *  and a site-wide strip about the beta on top of that reads as a warning
 *  attached to the thing they just bought. Matched by prefix, because the page
 *  is reached both as `/order/success/:id` and as `/order/success?pi=…`. */
const NO_BANNER_PREFIXES: string[] = [ERoutes.OrderSuccess];

const bannerIsUnwelcome = (pathname: string) =>
  NO_BANNER_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const BetaGlobalBanner: React.FC<{ placement?: "top" | "bottom" }> = ({ placement = "top" }) => {
  // Read synchronously, before the first paint. It used to start `true` and be
  // corrected by an effect, which was harmless while dismissed meant "render
  // nothing" — and stopped being harmless the moment dismissed came to mean
  // "render at the bottom". Every fresh visit then painted the notice above the
  // footer for a frame before it jumped to the top.
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [fadingOut, setFadingOut] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const atBottom = placement === "bottom";
  const { pathname } = useLocation();
  // The home page used to be excluded outright, because a strip asking for bug
  // reports is the wrong first sentence to read about a product. That reasoning
  // was about the ask, and home no longer carries it — it gets the welcome,
  // which is a fair first sentence.
  const asks = wantsTheAsk(pathname);

  // Read once, not per route: nothing about navigating changes the answer, and
  // starting dismissed avoids a flash of the banner before the flag is known.
  useEffect(() => {
    const read = () => {
      try {
        setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
      } catch {
        setDismissed(false);
      }
    };
    read();
    window.addEventListener(MOVED_EVENT, read);
    return () => window.removeEventListener(MOVED_EVENT, read);
  }, []);

  const showBanner = bannerIsUnwelcome(pathname)
    ? false
    : atBottom ? dismissed : !dismissed && !NO_TOP_ROUTES.includes(pathname);

  // Only the top placement is out of flow and needs its strip reserved.
  useBannerOffset("--beta-banner-h", showBanner && !atBottom, bannerRef);

  if (!showBanner) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch { /* non-fatal — it simply returns to the top next visit */ }
    setFadingOut(true);
    window.setTimeout(() => {
      setDismissed(true);
      setFadingOut(false);
      // After the collapse, not during it, so the notice is never in two
      // places at once.
      window.dispatchEvent(new Event(MOVED_EVENT));
    }, 360);
  };

  return (
    <div
      data-banner-surface
      ref={bannerRef}
      data-global-banner
      className="beta-banner"
      style={{
        width: "100%",
        ...bannerSurface("rgb(41, 98, 155)"),
        // Both notices are a single line at every width. Everything that takes
        // horizontal room — the side padding, the gap, the two type sizes, the
        // X's inset — is a clamp rather than a fixed value, because these are
        // inline styles and cannot carry a media query. They hold their design
        // sizes from roughly 410px up and only give ground on the narrowest
        // phones, where a wrap would otherwise put the icon on its own line.
        padding: fadingOut ? "0 clamp(10px, 3vw, 24px)" : "12px clamp(10px, 3vw, 24px)",
        marginTop: atBottom ? 0 : -1,
        maxHeight: fadingOut ? 0 : 200,
        opacity: fadingOut ? 0 : 1,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "nowrap",
        gap: "clamp(6px, 2vw, 12px)",
        color: "#fff",
        fontFamily: '"Raleway", sans-serif',
        fontSize: "0.875rem",
        position: "relative",
        transition:
          "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {!asks && (
        <svg
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="#ffd75e"
          stroke="none"
          style={{ flexShrink: 0, marginRight: -4 }}
          aria-hidden="true"
        >
          <path d="M12 2.8 14.6 8.7 21 9.4l-4.8 4.3 1.3 6.3L12 16.9 6.5 20l1.3-6.3L3 9.4l6.4-.7Z" />
        </svg>
      )}
      <span
        style={{
          fontWeight: 500,
          whiteSpace: "nowrap",
          fontSize: asks ? "clamp(0.72rem, 3.1vw, 0.875rem)" : "clamp(0.75rem, 3.4vw, 0.875rem)",
        }}
      >
        {asks ? "Make Audio Sanctum better" : "Welcome to the new Audio Sanctum"}
      </span>
      {asks && (
      <Link
        to={ERoutes.Contact}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "clamp(4px, 1.6vw, 7px)",
          padding: "2px 10px",
          borderRadius: 6,
          background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
          border: "none",
          color: "#2a1d14",
          flexShrink: 0,
          fontFamily: '"Raleway", sans-serif',
          fontSize: "clamp(0.68rem, 2.9vw, 0.8125rem)",
          fontWeight: 600,
          textDecoration: "none",
          cursor: "pointer",
          whiteSpace: "nowrap",
          boxShadow: "0 0 12px rgba(186, 144, 92, 0.25)",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(186, 144, 92, 0.5)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 0 12px rgba(186, 144, 92, 0.25)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="m8 2 1.88 1.88" />
          <path d="M14.12 3.88 16 2" />
          <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
          <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
          <path d="M12 20v-9" />
          <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
          <path d="M6 13H2" />
          <path d="M6.53 17c-1.93.2-3.53 1.9-3.53 4" />
          <path d="M17.47 9c1.93-.2 3.53-1.9 3.53-4" />
          <path d="M18 13h4" />
          <path d="M17.47 17c1.93.2 3.53 1.9 3.53 4" />
        </svg>
        Report a bug
      </Link>
      )}
      {!atBottom && (
      <button
        onClick={handleDismiss}
        aria-label="Move to the bottom of the page"
        className="banner-dismiss"
        style={{
          position: "absolute",
          top: "50%",
          right: "clamp(4px, 2vw, 16px)",
          transform: "translateY(-50%)",
          width: 32,
          height: 32,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.98)",
          cursor: "pointer",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "color 0.15s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.98)"; }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      )}
    </div>
  );
};

export default BetaGlobalBanner;
